import {
  ActivityLogManyQuery,
  BroadcastManyQuery,
  EnumActivityLogType,
  EnumEntityReferenceType,
  EnumSurveyResponseAdminStatus,
  EnumSurveyResponseReviewStatus,
  EnumSurveyResponseStatus,
  SortFindManyActivityLogInput,
  useActivityLogManyLazyQuery,
  useBroadcastManyLazyQuery,
} from "gql/generated";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  ActivityLogFilter,
  getTypesForFilters,
} from "shared/utils";
import { useFeatureFlag } from "shared/contextProviders/featureFlags";
import { FeatureFlags } from "shared/featureFlags";

export type ReadStatusFilter = "READ" | "UNREAD";

type ActivityLogItem = ActivityLogManyQuery["activityLogMany"][0];

export type UseActivityLogFeedOptions = {
  batchSize?: number;
  ownerFilter?: Record<string, any>;
  clientFilter?: (logs: ActivityLogItem[]) => ActivityLogItem[];
  /** Extra entity navigation handled by the caller (return true to stop propagation). */
  onEntityClick?: (entity: { type: string; _entityId: string }, log: ActivityLogItem) => boolean;
};

const FORM_LOG_TYPES: EnumActivityLogType[] = [
  EnumActivityLogType.FormCompleted,
  EnumActivityLogType.FormDraftSaved,
  EnumActivityLogType.FormResubmitted,
  EnumActivityLogType.FormSubmissionApproved,
  EnumActivityLogType.FormSubmissionRejected,
];

/** Log types that open SurveyResponseViewer when surveyResponseId is set */
const SURVEY_RESPONSE_SLIDEOUT_LOG_TYPES: EnumActivityLogType[] = [
  ...FORM_LOG_TYPES,
  EnumActivityLogType.InquiryReceived,
];

export function useActivityLogFeed(opts: UseActivityLogFeedOptions = {}) {
  const {
    batchSize = 200,
    ownerFilter,
    clientFilter,
    onEntityClick,
  } = opts;

  const navigate = useNavigate();
  const formsEnabled = useFeatureFlag(FeatureFlags.FormsInBroadcasts);

  const activityLogsRef = useRef<ActivityLogItem[]>([]);
  const fetchedSkipRef = useRef(0);
  const [activityLogs, setActivityLogs] = useState<ActivityLogItem[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const fetchGenRef = useRef(0);

  // --- Filters ---
  const [selectedFilters, setSelectedFilters] = useState<ActivityLogFilter[]>(["ALL"]);
  const selectedFiltersRef = useRef<ActivityLogFilter[]>(["ALL"]);
  useEffect(() => { selectedFiltersRef.current = selectedFilters; }, [selectedFilters]);

  const [selectedTagFilters, setSelectedTagFilters] = useState<string[]>([]);
  const selectedTagFiltersRef = useRef<string[]>([]);
  useEffect(() => { selectedTagFiltersRef.current = selectedTagFilters; }, [selectedTagFilters]);

  const [readStatusFilters, setReadStatusFilters] = useState<ReadStatusFilter[]>([]);

  // --- Apollo ---
  const [getActivityLogs] = useActivityLogManyLazyQuery();
  const [getBroadcastData] = useBroadcastManyLazyQuery();

  // --- Data fetch ---
  const fetchLogs = useCallback(
    async (skip: number, filters: ActivityLogFilter[], tagIds?: string[]) => {
      const types = getTypesForFilters(filters);
      const filter: Record<string, any> = {
        ...ownerFilter,
      };
      if (types.length > 0) filter.types = types;
      if (tagIds && tagIds.length > 0) filter.tags = tagIds;
      const response = await getActivityLogs({
        variables: {
          ...(Object.keys(filter).length > 0 ? { filter } : {}),
          limit: batchSize,
          skip,
          sort: SortFindManyActivityLogInput.CreatedDesc,
        },
        fetchPolicy: "network-only",
      });
      return response.data?.activityLogMany ?? [];
    },
    [getActivityLogs, ownerFilter, batchSize],
  );

  const applyClientFilter = useCallback(
    (logs: ActivityLogItem[]) => clientFilter ? clientFilter(logs) : logs,
    [clientFilter],
  );

  /**
   * Walk past client-filtered rows using the server skip cursor, not the
   * visible list length. Otherwise a noisy first page looks empty and "load
   * more" re-requests the same slice.
   */
  const fetchVisiblePage = useCallback(
    async (
      startSkip: number,
      filters: ActivityLogFilter[],
      tagIds: string[] | undefined,
      existing: ActivityLogItem[],
    ) => {
      let skip = startSkip;
      let visible = [...existing];
      let lastPageLen = 0;
      const targetCount = existing.length + batchSize;
      let pages = 0;
      do {
        const logs = await fetchLogs(skip, filters, tagIds);
        lastPageLen = logs.length;
        skip += logs.length;
        visible = [...visible, ...applyClientFilter(logs)];
        pages += 1;
      } while (
        visible.length < targetCount &&
        lastPageLen === batchSize &&
        pages < 15
      );
      return {
        visible,
        nextSkip: skip,
        hasMore: lastPageLen === batchSize,
      };
    },
    [fetchLogs, applyClientFilter, batchSize],
  );

  useEffect(() => {
    const gen = ++fetchGenRef.current;
    const load = async () => {
      setInitialLoading(true);
      fetchedSkipRef.current = 0;
      const page = await fetchVisiblePage(0, selectedFiltersRef.current, undefined, []);
      if (gen !== fetchGenRef.current) return;
      fetchedSkipRef.current = page.nextSkip;
      activityLogsRef.current = page.visible;
      setActivityLogs(page.visible);
      setHasMore(page.hasMore);
      setInitialLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchLogs, batchSize, fetchVisiblePage]);

  const fetchMore = useCallback(async () => {
    setIsFetchingMore(true);
    const page = await fetchVisiblePage(
      fetchedSkipRef.current,
      selectedFiltersRef.current,
      selectedTagFiltersRef.current.length > 0
        ? selectedTagFiltersRef.current
        : undefined,
      activityLogsRef.current,
    );
    fetchedSkipRef.current = page.nextSkip;
    activityLogsRef.current = page.visible;
    setActivityLogs(page.visible);
    setHasMore(page.hasMore);
    setIsFetchingMore(false);
  }, [fetchVisiblePage]);

  const refetchWithFilters = useCallback(
    async (filters: ActivityLogFilter[], tagIds: string[]) => {
      setInitialLoading(true);
      fetchedSkipRef.current = 0;
      const page = await fetchVisiblePage(
        0,
        filters,
        tagIds.length > 0 ? tagIds : undefined,
        [],
      );
      fetchedSkipRef.current = page.nextSkip;
      activityLogsRef.current = page.visible;
      setActivityLogs(page.visible);
      setHasMore(page.hasMore);
      setInitialLoading(false);
    },
    [fetchVisiblePage],
  );

  // --- Filter handlers ---
  const handleFilterClick = useCallback(
    async (filter: ActivityLogFilter) => {
      const current = selectedFiltersRef.current;
      const isRemoving = current.includes(filter);
      let next: ActivityLogFilter[];
      if (isRemoving) {
        next = current.filter(f => f !== filter);
        if (next.length === 0) next = ["ALL"];
      } else {
        next = [...current.filter(f => f !== "ALL"), filter];
      }
      selectedFiltersRef.current = next;
      setSelectedFilters(next);

      if (!next.includes("PERSON")) {
        selectedTagFiltersRef.current = [];
        setSelectedTagFilters([]);
        setReadStatusFilters([]);
      }

      await refetchWithFilters(next, selectedTagFiltersRef.current);
    },
    [refetchWithFilters],
  );

  const handleTagFilterClick = useCallback(
    async (tagId: string) => {
      const current = selectedTagFiltersRef.current;
      const next = current.includes(tagId)
        ? current.filter(id => id !== tagId)
        : [...current, tagId];
      selectedTagFiltersRef.current = next;
      setSelectedTagFilters(next);
      await refetchWithFilters(selectedFiltersRef.current, next);
    },
    [refetchWithFilters],
  );

  const handleReadStatusFilterClick = useCallback((status: ReadStatusFilter) => {
    setReadStatusFilters(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status],
    );
  }, []);

  // --- Client-side read/unread filter ---
  const filteredActivityLogs = useMemo(() => {
    if (readStatusFilters.length === 0) return activityLogs;
    return activityLogs.filter(log => {
      const adminStatus = log.surveyResponse?.adminStatus;
      if (!adminStatus) return true;
      return readStatusFilters.includes(
        adminStatus === EnumSurveyResponseAdminStatus.Unread ? "UNREAD" : "READ",
      );
    });
  }, [activityLogs, readStatusFilters]);

  const hasUnreadInquiries = useMemo(
    () => activityLogs.some(log => log.surveyResponse?.adminStatus === EnumSurveyResponseAdminStatus.Unread),
    [activityLogs],
  );

  // --- Modal / click state ---
  const [selectedBroadcast, setSelectedBroadcast] = useState<
    BroadcastManyQuery["broadcastMany"][0] | null
  >(null);
  const [modalOpen, setModalOpen] = useState<{
    type: string;
    _entityId: string;
  } | null>(null);
  const [loadingActivityItemId, setLoadingActivityItemId] = useState<string | null>(null);
  const [selectedEmailActivityLog, setSelectedEmailActivityLog] = useState<ActivityLogItem | null>(null);
  const [selectedSurveyResponseId, setSelectedSurveyResponseId] = useState<string | null>(null);

  const handleActivityFeedClick = useCallback(
    async (activityLog: ActivityLogItem) => {
      const typeStr = String(activityLog.type ?? "");

      if (
        activityLog.surveyResponseId &&
        SURVEY_RESPONSE_SLIDEOUT_LOG_TYPES.includes(activityLog.type) &&
        (formsEnabled ||
          activityLog.type === EnumActivityLogType.InquiryReceived)
      ) {
        setSelectedSurveyResponseId(activityLog.surveyResponseId);
        return;
      }

      if (
        activityLog.type === EnumActivityLogType.EmailDailyDigestSent ||
        activityLog.type === EnumActivityLogType.EmailInviteSent ||
        activityLog.type === EnumActivityLogType.EmailBroadcastNotificationSent ||
        activityLog.type === EnumActivityLogType.EmailRenewalSent ||
        activityLog.type === EnumActivityLogType.EmailPaymentRequestSent ||
        typeStr === "EMAIL_DAILY_DIGEST_SENT" ||
        typeStr === "EMAIL_INVITE_SENT" ||
        typeStr === "EMAIL_BROADCAST_NOTIFICATION_SENT" ||
        typeStr === "EMAIL_RENEWAL_SENT" ||
        typeStr === "EMAIL_PAYMENT_REQUEST_SENT"
      ) {
        setSelectedEmailActivityLog(activityLog);
        return;
      }

      const entity = activityLog.entity;
      if (!entity) return;

      if (onEntityClick?.(entity, activityLog)) return;

      switch (entity.type) {
        case EnumEntityReferenceType.Operator:
          navigate(
            `/app/organization/${activityLog.owner?.entityId ?? ""}/directory/${entity._entityId}`,
          );
          break;
        case EnumEntityReferenceType.Person:
          setModalOpen(entity);
          break;
        case EnumEntityReferenceType.Event:
          setModalOpen(entity);
          break;
        case EnumEntityReferenceType.Broadcast: {
          setLoadingActivityItemId(activityLog._id);
          const { data: broadcastData } = await getBroadcastData({
            variables: { filter: { _id: entity._entityId } },
          });
          if (broadcastData?.broadcastMany?.length) {
            setSelectedBroadcast(broadcastData.broadcastMany[0]);
            setModalOpen(entity);
          }
          setLoadingActivityItemId(null);
          break;
        }
      }
    },
    [navigate, getBroadcastData, onEntityClick, formsEnabled],
  );

  const updateLogAdminStatus = useCallback(
    (surveyResponseId: string, adminStatus: EnumSurveyResponseAdminStatus) => {
      const patch = (logs: ActivityLogItem[]) =>
        logs.map(log =>
          log.surveyResponseId === surveyResponseId
            ? { ...log, surveyResponse: { ...log.surveyResponse, _id: surveyResponseId, adminStatus } }
            : log,
        );
      activityLogsRef.current = patch(activityLogsRef.current);
      setActivityLogs(prev => patch(prev));
    },
    [],
  );

  const updateLogTags = useCallback(
    (surveyResponseId: string, tags: string[]) => {
      const patch = (logs: ActivityLogItem[]) =>
        logs.map(log =>
          log.surveyResponseId === surveyResponseId
            ? { ...log, tags, surveyResponse: { ...log.surveyResponse, _id: surveyResponseId, tags } }
            : log,
        );
      activityLogsRef.current = patch(activityLogsRef.current);
      setActivityLogs(prev => patch(prev));
    },
    [],
  );

  const updateLogReviewStatus = useCallback(
    (surveyResponseId: string, newStatus: string) => {
      const status = newStatus === "SUBMITTED"
        ? EnumSurveyResponseStatus.Submitted
        : EnumSurveyResponseStatus.Rejected;
      const reviewStatus = newStatus === "SUBMITTED"
        ? EnumSurveyResponseReviewStatus.Approved
        : EnumSurveyResponseReviewStatus.Rejected;
      const patch = (logs: ActivityLogItem[]) =>
        logs.map(log =>
          log.surveyResponseId === surveyResponseId
            ? { ...log, surveyResponse: { ...log.surveyResponse, _id: surveyResponseId, status, reviewStatus } }
            : log,
        );
      activityLogsRef.current = patch(activityLogsRef.current);
      setActivityLogs(prev => patch(prev));
    },
    [],
  );

  const refetch = useCallback(async () => {
    await refetchWithFilters(selectedFiltersRef.current, selectedTagFiltersRef.current);
  }, [refetchWithFilters]);

  return {
    loading: initialLoading,
    isFetchingMore,
    activityLogs: filteredActivityLogs,
    rawActivityLogs: activityLogs,
    loadedCount: activityLogsRef.current.length,
    fetchMore,
    refetch,
    hasMore,
    selectedFilters,
    onFilterClick: handleFilterClick,
    selectedTagFilters,
    onTagFilterClick: handleTagFilterClick,
    readStatusFilters,
    onReadStatusFilterClick: handleReadStatusFilterClick,
    onActivityFeedClick: handleActivityFeedClick,
    modalOpen,
    setModalOpen,
    loadingActivityItemId,
    selectedBroadcast,
    selectedEmailActivityLog,
    setSelectedEmailActivityLog,
    selectedSurveyResponseId,
    setSelectedSurveyResponseId,
    updateLogAdminStatus,
    updateLogTags,
    updateLogReviewStatus,
    hasUnreadInquiries,
    formsEnabled,
  };
}
